/**
 * Preview Inspector Script
 *
 * Enables inspect mode with interactive chat bubbles.
 * Shows element details with upload and regenerate actions.
 */

export const PREVIEW_INSPECTOR_SCRIPT = `
<script>
(function() {
  let inspectMode = false;
  let highlightElement = null;
  let bubble = null;
  let selectedElement = null;

  function isTaskingUi(el) {
    return !!(el && typeof el.closest === 'function' && el.closest('.tasking-chat-bubble'));
  }

  function resolveInspectableElement(target) {
    if (!target) return null;
    const node = target.nodeType === Node.TEXT_NODE ? target.parentElement : target;
    if (!node || node === document.body || node === document.documentElement) return null;
    if (!(node instanceof Element)) return null;
    if (isTaskingUi(node)) return null;
    return node;
  }

  // Strip tasking-* classes from class strings to avoid polluting element info
  function cleanClasses(className) {
    if (!className) return '';
    if (typeof className !== 'string') return '';
    return className.split(/\\s+/).filter(c => !c.startsWith('tasking-')).join(' ');
  }

  const styleEl = document.createElement('style');
  styleEl.textContent = \`
    .tasking-inspect-highlight {
      outline: 2px solid #C9822A !important;
      outline-offset: -2px !important;
      background: rgba(201, 130, 42, 0.08) !important;
    }
    .tasking-chat-bubble {
      position: fixed;
      background: #18181b;
      color: #fafafa;
      padding: 16px;
      border-radius: 12px;
      font-size: 12px;
      font-family: system-ui, -apple-system, sans-serif;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
      border: 1px solid #27272a;
      max-width: 320px;
      z-index: 99999;
      pointer-events: auto;
    }
    .tasking-bubble-title {
      font-weight: 600;
      margin-bottom: 8px;
      color: #fafafa;
      font-size: 14px;
    }
    .tasking-bubble-info {
      background: rgba(255, 255, 255, 0.06);
      padding: 8px;
      border-radius: 8px;
      margin-bottom: 12px;
      font-family: 'Courier New', monospace;
      font-size: 12px;
      overflow-x: auto;
      white-space: pre-wrap;
      word-break: break-word;
      color: #a1a1aa;
    }
    .tasking-bubble-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .tasking-bubble-btn {
      flex: 1;
      min-width: 80px;
      min-height: 36px;
      padding: 8px 12px;
      background: #C9822A;
      color: #fff;
      border: none;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: background 0.15s, transform 0.1s;
    }
    .tasking-bubble-btn:hover {
      background: #d4921e;
    }
    .tasking-bubble-btn:active {
      background: #b8741f;
      transform: scale(0.97);
    }
    .tasking-bubble-btn.close {
      background: #3f3f46;
      color: #a1a1aa;
      flex: 0;
      min-width: 36px;
    }
    .tasking-bubble-btn.close:hover {
      background: #52525b;
      color: #fafafa;
    }
    .tasking-file-input {
      display: none;
    }
  \`;
  document.head.appendChild(styleEl);

  window.addEventListener('message', (e) => {
    if (e.data && e.data.type === 'inspect-mode') {
      inspectMode = e.data.enabled;

      if (inspectMode) {
        document.body.style.cursor = 'crosshair';
        document.addEventListener('pointerover', handleMouseOver, true);
        document.addEventListener('pointerdown', handleElementPointerDown, true);
        document.addEventListener('click', swallowClickWhileInspecting, true);
      } else {
        document.body.style.cursor = '';
        document.removeEventListener('pointerover', handleMouseOver, true);
        document.removeEventListener('pointerdown', handleElementPointerDown, true);
        document.removeEventListener('click', swallowClickWhileInspecting, true);
        if (highlightElement) {
          highlightElement.classList.remove('tasking-inspect-highlight');
          highlightElement = null;
        }
        if (bubble) {
          bubble.remove();
          bubble = null;
        }
      }
    }
  });

  function handleMouseOver(e) {
    if (!inspectMode) return;
    const el = resolveInspectableElement(e.target);
    if (el) {
      if (highlightElement && highlightElement !== el) {
        highlightElement.classList.remove('tasking-inspect-highlight');
      }
      el.classList.add('tasking-inspect-highlight');
      highlightElement = el;
    }
  }

  function handleElementPointerDown(e) {
    if (!inspectMode) return;
    const el = resolveInspectableElement(e.target);
    if (!el) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }

    selectedElement = el;
    showBubble(el);
  }

  function swallowClickWhileInspecting(e) {
    if (!inspectMode) return;
    const el = resolveInspectableElement(e.target);
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  }

  function showBubble(el) {
    if (bubble) {
      bubble.remove();
    }

    const rect = el.getBoundingClientRect();
    const styles = window.getComputedStyle(el);

    const tag = el.tagName.toLowerCase();
    const id = el.id ? \` id="\${el.id}"\` : '';
    const cleaned = cleanClasses(el.className);
    const classes = cleaned ? \` class="\${cleaned}"\` : '';
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);

    let info = \`<\${tag}\${id}\${classes}>\\n\`;
    info += \`\${width}x\${height}px\\n\`;
    info += \`bg: \${styles.backgroundColor}\\n\`;
    info += \`color: \${styles.color}\`;

    bubble = document.createElement('div');
    bubble.className = 'tasking-chat-bubble';

    const title = document.createElement('div');
    title.className = 'tasking-bubble-title';
    title.textContent = 'Edit Element';

    const infoBox = document.createElement('div');
    infoBox.className = 'tasking-bubble-info';
    infoBox.textContent = info;

    const actions = document.createElement('div');
    actions.className = 'tasking-bubble-actions';

    const uploadBtn = document.createElement('button');
    uploadBtn.className = 'tasking-bubble-btn';
    uploadBtn.textContent = 'Upload';
    uploadBtn.onclick = (e) => {
      e.stopPropagation();
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';
      fileInput.onchange = (evt) => {
        const file = evt.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (e) => {
            window.parent.postMessage({
              type: 'element-action',
              action: 'upload',
              element: getElementInfo(el),
              imageData: e.target.result,
            }, '*');
          };
          reader.readAsDataURL(file);
        }
      };
      fileInput.click();
    };

    const regenBtn = document.createElement('button');
    regenBtn.className = 'tasking-bubble-btn';
    regenBtn.textContent = 'Regen';
    regenBtn.onclick = (e) => {
      e.stopPropagation();
      window.parent.postMessage({
        type: 'element-action',
        action: 'regenerate',
        element: getElementInfo(el),
      }, '*');
    };

    const closeBtn = document.createElement('button');
    closeBtn.className = 'tasking-bubble-btn close';
    closeBtn.textContent = '\\u2715';
    closeBtn.onclick = (e) => {
      e.stopPropagation();
      if (bubble) {
        bubble.remove();
        bubble = null;
      }
    };

    actions.appendChild(uploadBtn);
    actions.appendChild(regenBtn);
    actions.appendChild(closeBtn);

    bubble.appendChild(title);
    bubble.appendChild(infoBox);
    bubble.appendChild(actions);

    document.body.appendChild(bubble);

    // Position bubble above element, fall back to below
    const bubbleRect = bubble.getBoundingClientRect();
    let top = rect.top - bubbleRect.height - 12;
    let left = rect.left + rect.width / 2 - bubbleRect.width / 2;

    if (top < 8) {
      top = rect.bottom + 12;
    }
    if (left < 8) left = 8;
    if (left + bubbleRect.width > window.innerWidth - 8) {
      left = window.innerWidth - bubbleRect.width - 8;
    }

    bubble.style.top = top + 'px';
    bubble.style.left = left + 'px';
  }

  function getElementInfo(el) {
    const rect = el.getBoundingClientRect();
    const styles = window.getComputedStyle(el);
    return {
      tag: el.tagName.toLowerCase(),
      id: el.id || '',
      classes: cleanClasses(el.className),
      text: el.textContent?.slice(0, 100) || '',
      path: buildCssPath(el),
      rect: {
        width: Math.round(rect.width),
        height: Math.round(rect.height),
        top: Math.round(rect.top),
        left: Math.round(rect.left),
      },
      styles: {
        color: styles.color,
        backgroundColor: styles.backgroundColor,
        fontSize: styles.fontSize,
        padding: styles.padding,
      },
    };
  }

  function buildCssPath(el) {
    const parts = [];
    let current = el;
    while (current && current !== document.body && current !== document.documentElement) {
      let selector = current.tagName.toLowerCase();
      if (current.id) {
        selector += '#' + current.id;
      } else if (current.className) {
        const cleaned = cleanClasses(current.className);
        if (cleaned) {
          selector += '.' + cleaned.split(/\\s+/).join('.');
        }
      }
      parts.unshift(selector);
      current = current.parentElement;
    }
    return parts.join(' > ');
  }
})();
</script>
`;
